import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ReactComponent as AddPhotoIcon } from '../../../Assets/add_photo_alternate.svg';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashAlt } from '@fortawesome/free-regular-svg-icons';
import { faPencilAlt, faCheck, faTimes } from '@fortawesome/free-solid-svg-icons';

import APIGet from '../../../API/APIGet';
import APIPost from '../../../API/APIPost';
import APIPut from '../../../API/APIPut';
import APIDelete from '../../../API/APIDelete';
import { fileToBase64 } from '../../../Helper/FileHelper';
import { toast } from 'react-toastify';

function extractPassportList(raw) {
	if (raw == null) return [];
	if (Array.isArray(raw)) return raw;
	if (Array.isArray(raw.data)) return raw.data;
	if (raw.data && Array.isArray(raw.data.passports)) return raw.data.passports;
	if (Array.isArray(raw.passports)) return raw.passports;
	return [];
}

function imageUrlFromApiItem(item) {
	const img = item.passportImage != null && item.passportImage !== '' ? item.passportImage : null;
	const fromBinary = item.imageBinary != null && item.imageBinary !== '' ? item.imageBinary : null;
	const raw = img || fromBinary || item.imageUrl || item.image;
	if (raw == null || raw === '') return '';
	const s = String(raw);
	if (s.startsWith('http') || s.startsWith('data:') || s.startsWith('blob:')) return s;
	return 'data:image/jpeg;base64,' + s;
}

function normalizePassportItem(item) {
	const id = item.id != null ? item.id : item.passportId;
	const remark = item.remark != null ? String(item.remark) : '';
	const image = imageUrlFromApiItem(item);
	return { id, remark, image };
}

export const PassportTab = ({ traveler, travelerId, MAX_IMAGE_BYTES }) => {
	const [passports, setPassports] = useState([]);
	const [isLoadingPassports, setIsLoadingPassports] = useState(false);
	const [deleteConfirmPassportId, setDeleteConfirmPassportId] = useState(null);
	const [isDeletingPassport, setIsDeletingPassport] = useState(false);

	const passportFileInputRef = useRef(null);

	const loadPassports = useCallback(async () => {
		if (!travelerId) return;
		setIsLoadingPassports(true);
		try {
			const result = await APIGet('travelers/' + travelerId + '/passports');
			const payload = result && result.data != null ? result.data : null;
			const list = extractPassportList(payload);
			setPassports(list.map(normalizePassportItem));
		} catch (e) {
			console.log(e);
			setPassports([]);
		} finally {
			setIsLoadingPassports(false);
		}
	}, [travelerId]);

	useEffect(() => {
		loadPassports();
	}, [loadPassports]);

	const handlePassportClick = () => {
		if (passportFileInputRef.current) {
			passportFileInputRef.current.click();
		}
	};

	const handleRemarkSave = async (passportId, remark) => {
		if (!travelerId || passportId == null) return;
		const request = await APIPut('travelers/' + travelerId + '/passports/' + passportId, { id: passportId, remark });
		if (request && request.status >= 200 && request.status < 300) {
			toast.success('บันทึกรายละเอียด Passport สำเร็จ');
			await loadPassports();
		} else {
			console.log('Failed to update passport remark', request);
			toast.error('ไม่สามารถบันทึกรายละเอียด Passport ได้ กรุณาลองใหม่อีกครั้ง');
		}
	};

	const handleReplacePassportImage = async (passportId, remark, file) => {
		if (!travelerId || passportId == null || !file) return;
		const imageBinary = await fileToBase64(file);
		if (file.size > MAX_IMAGE_BYTES) {
			toast.error('ขนาดรูปภาพมีขนาดใหญ่เกินไป');
			if (passportFileInputRef.current) passportFileInputRef.current.value = '';
			return;
		}
		const request = await APIPut('travelers/' + travelerId + '/passports/' + passportId, { id: passportId, remark, imageBinary });
		if (request && request.status >= 200 && request.status < 300) {
			await loadPassports();
			toast.success('บันทึกรูปภาพ Passport สำเร็จ');
		} else {
			console.log('Failed to update passport image', request);
			toast.error('ไม่สามารถบันทึกรูปภาพ Passport ได้ กรุณาลองใหม่อีกครั้ง');
		}
	};

	const confirmDeletePassport = async passportId => {
		if (!travelerId || passportId == null) return;
		setIsDeletingPassport(true);
		try {
			const result = await APIDelete('travelers/' + travelerId + '/passports/' + passportId, {});
			if (result && result.status >= 200 && result.status < 300) {
				setDeleteConfirmPassportId(null);
				await loadPassports();
				toast.success('ลบ Passport สำเร็จ');
			} else {
				console.log('Failed to delete passport', result);
				toast.error('ไม่สามารถลบ Passport ได้ กรุณาลองใหม่อีกครั้ง');
			}
		} catch (e) {
			console.log(e);
		} finally {
			setIsDeletingPassport(false);
		}
	};

	const handleUploadPassport = async event => {
		const file = event.target.files && event.target.files[0];
		if (!file || !travelerId) return;
		if (file.size > MAX_IMAGE_BYTES) {
			toast.error('ขนาดรูปภาพมีขนาดใหญ่เกินไป');
			if (passportFileInputRef.current) passportFileInputRef.current.value = '';
			return;
		}
		try {
			const imageBinary = await fileToBase64(file);
			const request = await APIPost('travelers/' + travelerId + '/passports', {
				imageBinary,
				remark: '',
			});
			if (request && request.status >= 200 && request.status < 300) {
				await loadPassports();
				toast.success('บันทึกรูปภาพ Passport สำเร็จ');
			} else {
				console.log('Failed to add passport', request);
				toast.error('ไม่สามารถบันทึกรูปภาพ Passport ได้ กรุณาลองใหม่อีกครั้ง');
			}
		} catch (e) {
			console.log(e);
		} finally {
			if (passportFileInputRef.current) passportFileInputRef.current.value = '';
		}
	};

	const passportsWithImages = passports.filter(p => p.image);
	const showOnlyAddPlaceholder = !isLoadingPassports && passportsWithImages.length === 0;

	return (
		<div className="traveler-docs">
			<div className="columns">
				<div className="column is-12" style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
					{isLoadingPassports ? <span style={{ alignSelf: 'center', color: '#888' }}>กำลังโหลด...</span> : null}
					{!isLoadingPassports && passportsWithImages.length > 0
						? passportsWithImages.map(passport => (
								<PassportCard
									key={passport.id != null ? String(passport.id) : passport.image}
									passport={passport}
									onRemarkSave={handleRemarkSave}
									onReplaceImage={handleReplacePassportImage}
									isDeleteConfirm={deleteConfirmPassportId != null && String(deleteConfirmPassportId) === String(passport.id)}
									onDeleteClick={() => setDeleteConfirmPassportId(passport.id)}
									onDeleteConfirm={() => confirmDeletePassport(passport.id)}
									onDeleteCancel={() => setDeleteConfirmPassportId(null)}
									isDeletingPassport={isDeletingPassport}
								/>
						  ))
						: null}
					<div
						className=""
						onClick={handlePassportClick}
						onKeyDown={e => {
							if (e.key === 'Enter' || e.key === ' ') {
								e.preventDefault();
								handlePassportClick();
							}
						}}
						role="button"
						tabIndex={0}
						style={{ width: 100, height: 100, marginLeft: 20, marginTop: 10, cursor: 'pointer' }}
						aria-label={showOnlyAddPlaceholder ? 'เพิ่มรูป' : undefined}
					>
						<AddPhotoIcon className="traveler-doc-image" style={{ color: 'green' }} />
						<input ref={passportFileInputRef} className="traveler-file-input" type="file" accept="image/*" onChange={handleUploadPassport} />
					</div>
				</div>
			</div>
		</div>
	);
};

const PassportCard = ({ passport, onRemarkSave, onReplaceImage, isDeleteConfirm, onDeleteClick, onDeleteConfirm, onDeleteCancel, isDeletingPassport }) => {
	const [isEditing, setIsEditing] = useState(false);
	const [draftRemark, setDraftRemark] = useState(passport.remark);
	const [fullImageSrc, setFullImageSrc] = useState(null);
	const replaceFileInputRef = useRef(null);

	useEffect(() => {
		setDraftRemark(passport.remark);
	}, [passport.remark]);

	const closeLightbox = useCallback(() => setFullImageSrc(null), []);

	useEffect(() => {
		if (!fullImageSrc) return undefined;
		const onKeyDown = e => {
			if (e.key === 'Escape') closeLightbox();
		};
		document.addEventListener('keydown', onKeyDown);
		document.body.style.overflow = 'hidden';
		return () => {
			document.removeEventListener('keydown', onKeyDown);
			document.body.style.overflow = '';
		};
	}, [fullImageSrc, closeLightbox]);

	const saveRemark = async () => {
		await onRemarkSave(passport.id, draftRemark);
		setIsEditing(false);
	};

	return (
		<div className="traveler-doc-card">
			{isDeleteConfirm ? (
				<div
					className="traveler-doc-remove-confirm"
					style={{ display: 'flex', justifyContent: 'center', gap: 8 }}
					onClick={event => event.stopPropagation()}
					role="presentation"
				>
					<button
						type="button"
						className="button is-small"
						style={{
							backgroundColor: '#DC1818',
							borderColor: '#DC1818',
							color: '#fff',
							opacity: isDeletingPassport ? 0.7 : 1,
						}}
						onClick={event => {
							event.stopPropagation();
							onDeleteConfirm();
						}}
						disabled={isDeletingPassport}
						aria-label="ลบ"
						title="ลบ"
					>
						<FontAwesomeIcon icon={faTrashAlt} />
					</button>
					<button
						type="button"
						className="button is-small"
						style={{
							backgroundColor: '#22c65b',
							borderColor: '#22c65b',
							color: '#fff',
							opacity: isDeletingPassport ? 0.7 : 1,
						}}
						onClick={event => {
							event.stopPropagation();
							onDeleteCancel();
						}}
						disabled={isDeletingPassport}
						aria-label="ยกเลิก"
						title="ยกเลิก"
					>
						<FontAwesomeIcon icon={faTimes} />
					</button>
				</div>
			) : (
				<button
					type="button"
					className="traveler-doc-remove"
					onClick={event => {
						event.stopPropagation();
						onDeleteClick();
					}}
					aria-label="ลบรูป"
					title="ลบรูป"
				>
					<FontAwesomeIcon icon={faTrashAlt} />
				</button>
			)}
			<button type="button" className="traveler-doc-image-btn" onClick={() => setFullImageSrc(passport.image)} aria-label="View passport image full size">
				<img className="traveler-doc-image" src={passport.image} alt="Passport" />
			</button>
			<button
				type="button"
				className="button is-small is-text"
				style={{ padding: '2px 0', fontSize: 12 }}
				onClick={() => replaceFileInputRef.current && replaceFileInputRef.current.click()}
			>
				เปลี่ยนรูป
			</button>
			<input
				ref={replaceFileInputRef}
				className="traveler-file-input"
				type="file"
				accept="image/*"
				style={{ display: 'none' }}
				onChange={async e => {
					const file = e.target.files && e.target.files[0];
					if (!file) return;
					try {
						await onReplaceImage(passport.id, isEditing ? draftRemark : passport.remark, file);
					} finally {
						if (replaceFileInputRef.current) replaceFileInputRef.current.value = '';
					}
				}}
			/>
			<div className="columns" style={{ marginTop: 5 }}>
				<div className="column is-10">
					{isEditing ? (
						<textarea
							className="input"
							type="text"
							value={draftRemark}
							placeholder="รายละเอียดอื่นๆ"
							style={{ minHeight: 100 }}
							onChange={event => setDraftRemark(event.target.value)}
						/>
					) : (
						<span style={{ color: passport.remark ? 'black' : '#999' }}>{passport.remark || 'รายละเอียดอื่นๆ'}</span>
					)}
				</div>
				<div className="column is-2">
					{isEditing ? (
						<button className="button is-small" style={{ backgroundColor: 'transparent', border: 'none' }} onClick={saveRemark}>
							<FontAwesomeIcon icon={faCheck} style={{ color: 'green', fontSize: 16 }} />
						</button>
					) : (
						<button className="button is-small" style={{ backgroundColor: 'transparent', border: 'none' }} onClick={() => setIsEditing(true)}>
							<FontAwesomeIcon icon={faPencilAlt} />
						</button>
					)}
				</div>
			</div>
			{fullImageSrc ? (
				<div className="traveler-doc-lightbox" onClick={closeLightbox} onKeyDown={e => e.key === 'Escape' && closeLightbox()} role="presentation">
					<button type="button" className="traveler-doc-lightbox-close" onClick={closeLightbox} aria-label="Close">
						×
					</button>
					<img className="traveler-doc-lightbox-img" src={fullImageSrc} alt="Passport full size" onClick={e => e.stopPropagation()} />
				</div>
			) : null}
		</div>
	);
};
